import { Routes, Route, Navigate } from "react-router-dom";
import { ThemeProvider } from "./context/ThemeContext";
import { AuthProvider, useAuth } from "./auth/AuthContext";
import ProtectedRoute from "./auth/ProtectedRoute";
import RoleRedirect from "./auth/RoleRedirect";
import Gate from "./auth/Gate";
import { can, getUserType, USER_TYPES } from "./config/roles";
import { ROUTE_PERMS } from "./config/routes";
import { Toaster } from "react-hot-toast";
import { BrandContext } from "./context/BrandContext";

import { ToastProvider } from "./components/ToastProvider";

// ---------- auth pages ----------
import LoginPage from "./pages/auth/LoginPage";
import RegisterPage from "./pages/auth/RegisterPage";
import VerifyOtpPage from "./pages/auth/VerifyOtpPage";
import Suspended from "./pages/auth/SuspendedPage";
import ForgotPasswordPage from "./pages/auth/ForgotPasswordPage";
import ResetPasswordPage from "./pages/auth/ResetPasswordPage";

// ---------- layouts ----------
import AdminLayout from "./layouts/AdminLayout";
import UserLayout from "./layouts/UserLayout";

// ---------- admin pages ----------
import AdminHome from "./pages/admin/AdminHome";
import AssetsPage from "./pages/admin/assets/AssetsPage";
import AdminSubscriptions from "./pages/admin/AdminSubscriptions";
import UserManagementPage from "./pages/admin/UserManagementPage";
import ReportsPage from "./pages/admin/ReportsPage";
import ProjectManagementPage from "./pages/admin/ProjectManagementPage";
import AdmminProfilePage from "./pages/admin/AdminProfilePage";
import SecurityPage from "./pages/admin/SecurityPage";
import AdminRevitPage from "./pages/admin/AdminRevitPage";
import EditorAccessSettings from "./pages/admin/EditorAccessSettings";

// ---------- user pages ----------
import ProjectsPage from "./pages/user/ProjectsPage";
import QuotesPage from "./pages/user/QuotesPage";
import SubscriptionPage from "./pages/user/SubscriptionPage";
import BrandSettings from "./pages/user/BrandSettings";
import ProfilePage from "./pages/user/ProfilePage";
import StaffPage from "./pages/user/StaffPage";
import NotificationsPage from "./pages/common/NotificationPage";

// dashboards per user type
import CompanyDashboard from "./pages/user/Dashboard/CompanyDashboard";
import IndividualDashboard from "./pages/user/Dashboard/IndividualDashboard";
import InvitedDashboard from "./pages/user/Dashboard/InvitedDashboard";
import AcceptInvitePage from "./pages/invites/AcceptInvitePage";
import SupportHelpPage from "./pages/user/SupportPage";
import NotFoundPage from "./pages/common/NotFoundPage";
// import CompanyPricingEngine from "./pages/user/CompanyPricingEngine.jsx._backup";
import OwnerQuotes from "./pages/user/QuoteManagement";
import TermsPage from "./pages/termspage";
import CeedLayout from "./layouts/CeedLayout";
import CeedDashboard from "./pages/ceed/Dashboard";
import PurchasePlansPage from "./pages/ceed/PurchasePlansPage";
import QuotesOversightPage from "./pages/ceed/QuotesOversightPage";
import PurchasePlansAdminPage from "./pages/admin/PurchasePlanAdminPage";
import CompanyAutoEstimator from "./pages/user/CompanyAutoEstimator";
import UserAutoEstimator from "./pages/user/UserAutoEstimator";
import OwnerQuoteEditor from "./pages/user/OwnerQuoteEditor";
import CompanyQuotePage from "./pages/user/CompanyQuotePage";
import AdminQuotePool from "./pages/admin/AdminQuotePool";
import CeedProfilePage from "./pages/ceed/CeedProfilePage";
import AdminMetricDetail from "./pages/admin/AdminMetricDetail";
import { useEffect, useState } from "react";
import EngineeringPlansPage from "./pages/user/EngineeringPlanPage";
import FurniturePage from "./pages/admin/FurniturePage";

function DashboardLanding() {
  const { user } = useAuth();
  const t = getUserType(user);
  if (t === USER_TYPES.OWNER) return <CompanyDashboard />;
  if (t === USER_TYPES.INVITED) return <InvitedDashboard />;
  return <IndividualDashboard />;
}

export default function App() {

  const [publicBrand, setPublicBrand] = useState(null);

  useEffect(() => {
    async function loadBrand() {
      try {
        const apiBase = process.env.REACT_APP_BACKEND_URL;
        if (!apiBase) {
          console.warn("REACT_APP_BACKEND_URL not set; skipping brand/public");
          return;
        }

        const res = await fetch(`${apiBase}/api/brand/public`, {
          credentials: "include",
        });
        if (!res.ok) return;

        const data = await res.json();

        const host = window.location.hostname;
        const isLocalhost =
          host === "localhost" ||
          host === "127.0.0.1" ||
          host === "0.0.0.0";

        const mainBase =
          process.env.REACT_APP_PANEL_URL || "panel.mydesignerplus.com";
        const globalHosts = new Set([mainBase, `www.${mainBase}`]);

        // 1) Handle invalid tenant subdomains (e.g. achem.mydesignerplus.com)
        if (!isLocalhost) {
          if (
            data?.hostStatus === "unknown_subdomain" ||
            // fallback for older backend without hostStatus:
            (!data?.brand &&
              host.endsWith(`.${mainBase}`) &&
              !globalHosts.has(host))
          ) {
            const targetOrigin = `${window.location.protocol}//${mainBase}`;
            const path =
              window.location.pathname +
              window.location.search +
              window.location.hash;

            // Hard redirect to global MD+ dashboard
            window.location.replace(targetOrigin + path);
            return;
          }
        }

        // 2) If no brand (global host, localhost, etc.), keep default MD+ branding
        if (!data?.brand) {
          return;
        }

        // 3) Apply organization branding
        setPublicBrand(data.brand);

        if (typeof window !== "undefined") {
          window.__PUBLIC_BRAND__ = data.brand;
        }

        // favicon
        if (data.brand.faviconUrl) {
          let link =
            document.querySelector("link[rel='icon']") ||
            document.createElement("link");
          link.rel = "icon";
          link.href = data.brand.faviconUrl;
          document.head.appendChild(link);
        }

        // title
        if (data.brand.companyName) {
          document.title = `Dashboard | ${data.brand.companyName}`;
        }
      } catch (e) {
        console.error("Failed to load public brand", e);
      }
    }

    loadBrand();
  }, []);


  return (
    <ThemeProvider>
      <BrandContext.Provider value={publicBrand}>
        <AuthProvider>
          <ToastProvider>
            <Toaster position="top-center" toastOptions={{ duration: 3000 }} />
            <Routes>
              {/* ---------- public ---------- */}
              <Route path="/login" element={<LoginPage />} />
              <Route path="/register" element={<RegisterPage />} />
              <Route path="/verify-otp" element={<VerifyOtpPage />} />
              <Route path="/account-suspended" element={<Suspended />} />
              <Route path="/forgot-password" element={<ForgotPasswordPage />} />
              <Route path="/reset-password" element={<ResetPasswordPage />} />
              <Route path="/invites/accept" element={<AcceptInvitePage />} />
              <Route path="/terms" element={<TermsPage />} />
              {/* ---------- role landing ---------- */}
              <Route path="/" element={<RoleRedirect />} />
              {/* ---------- admin ---------- */}
              <Route path="/admin" element={ <ProtectedRoute roles={["admin"]}> <AdminLayout /> </ProtectedRoute> } >
                <Route index element={ <Gate perm={ROUTE_PERMS["/admin"]}><AdminHome /></Gate>}/>
                <Route path="metrics/:type" element={<AdminMetricDetail />} />
                <Route path="users" element={<Gate perm={ROUTE_PERMS["/admin/users"]}><UserManagementPage /></Gate>}/>
                <Route path="projects" element={<Gate perm={ROUTE_PERMS["/admin/projects"]}><ProjectManagementPage /></Gate>}/>
                <Route path="furnitures" element={ <Gate perm={ROUTE_PERMS["/admin/furnitures"]}> <FurniturePage /> </Gate> }/>
                <Route path="assets" element={ <Gate perm={ROUTE_PERMS["/admin/assets"]}> <AssetsPage /> </Gate> }/>
                <Route path="subscriptions" element={ <Gate perm={ROUTE_PERMS["/admin/subscriptions"]}> <AdminSubscriptions /> </Gate> } />
                <Route path="purchase-plans" element={ <Gate perm={ROUTE_PERMS["/admin/purchase-plans"]}> <PurchasePlansAdminPage /> </Gate> } />
                <Route path="reports" element={ <Gate perm={ROUTE_PERMS["/admin/reports"]}> <ReportsPage /> </Gate> } />
                <Route path="quotes" element={ <Gate perm={ROUTE_PERMS["/admin/quotes"]}> <AdminQuotePool /> </Gate> }/>
                <Route path="notifications" element={<NotificationsPage />} />
                {/* <Route path="materials" element={<MaterialsPage />} />
                <Route path="openings" element={<OpeningsPage />} /> */}
                <Route path="profile" element={<AdmminProfilePage />} />
                <Route path="security" element={ <Gate perm={ROUTE_PERMS["/admin/security"]}> <SecurityPage /> </Gate> } />
                <Route path="revit" element={<Gate perm={ROUTE_PERMS["/admin/revit"]}><AdminRevitPage /></Gate>} />
                <Route path="editor-settings" element={<Gate perm={ROUTE_PERMS["/admin/editor-settings"]}><EditorAccessSettings /></Gate>} />
              </Route>
              {/* ---------- ceed manager ---------- */}
              <Route path="/ceed" element={ <ProtectedRoute roles={["manager", "admin"]}> <CeedLayout /> </ProtectedRoute> }>
                <Route index element={ <Gate perm={ROUTE_PERMS["/ceed"]}> <CeedDashboard /> </Gate> } />
                <Route path="projects" element={<Gate perm={ROUTE_PERMS["/ceed/projects"]}><ProjectManagementPage /></Gate>}/>
                <Route path="assets" element={ <Gate perm={"ceed.assets"}> <AssetsPage /> </Gate> }/>
                <Route path="subscriptions" element={ <Gate perm={"ceed.subscriptions"}> <AdminSubscriptions /> </Gate> } />
                <Route path="purchase-plans" element={ <Gate perm={"ceed.purchaseplans"}> <PurchasePlansPage /> </Gate> }/>
                <Route path="quotes" element={ <Gate perm={"ceed.quotes"}> <QuotesOversightPage /> </Gate> } />
                <Route path="reports" element={ <Gate perm={"ceed.reports"}> <ReportsPage /> </Gate> } />
                <Route path="profile" element={ <Gate perm={ROUTE_PERMS["/ceed/profile"]}> <CeedProfilePage /> </Gate> } />
                <Route path="notifications" element={ <Gate perm={ROUTE_PERMS["/ceed/notifications"]}> <NotificationsPage /> </Gate> } />
              </Route>

              {/* ---------- app (users: individual/owner/invited/etc.) ---------- */}
              <Route path="/dashboard" element={ <ProtectedRoute roles={["user", "owner", "invited", "staff"]}> <UserLayout /> </ProtectedRoute> }>
                <Route index element={ <Gate perm={ROUTE_PERMS["/dashboard"]}> <DashboardLanding /> </Gate>}/>
                <Route path="projects" element={<ProjectsPage />} />
                <Route path="engineering-plans" element={<Gate perm={ROUTE_PERMS["/dashboard/engineering-plans"]}> <EngineeringPlansPage/></Gate>}/>
                <Route path="quotes" element={ <Gate perm={ROUTE_PERMS["/dashboard/quotes"]}> <QuotesPage /></Gate>}/>
                <Route path="myquotes" element={ <Gate perm={ROUTE_PERMS["/dashboard/myquotes"]}><CompanyQuotePage /></Gate>}/>
                <Route path="branding" element={ <Gate perm={ROUTE_PERMS["/dashboard/branding"]}> <BrandSettings /> </Gate> }/>
                <Route path="branding-view" element={ <Gate perm={ROUTE_PERMS["/dashboard/branding-view"]}> <BrandSettings readOnly /> </Gate> }/>
                <Route path="staff" element={ <Gate perm={ROUTE_PERMS["/dashboard/staff"]}> <StaffPage /> </Gate> } />
                <Route path="autoestimator" element={ <Gate perm={ROUTE_PERMS["/dashboard/autoestimator"]}> <CompanyAutoEstimator /> </Gate> }/>
                <Route path="estimator" element={ <Gate perm={ROUTE_PERMS["/dashboard/estimator"]}> <UserAutoEstimator /> </Gate> }/>
                <Route path="subscription" element={ <Gate perm={ROUTE_PERMS["/dashboard/subscription"]}> <SubscriptionPage /> </Gate> }/>
                <Route path="quotes-org" element={ <Gate perm={ROUTE_PERMS["/dashboard/quotes-org"]}> <OwnerQuotes /> </Gate> } />
                <Route path="quotes/:quoteId" element={ <Gate perm={ROUTE_PERMS["/dashboard/quotes/:quoteId"]}> <OwnerQuoteEditor /> </Gate> } />
                <Route path="support" element={<SupportHelpPage />} />
                <Route path="profile" element={<ProfilePage />} />
                <Route path="notifications" element={<NotificationsPage />} />
              </Route>
              {/* ---------- 404 ---------- */}
              <Route path="*" element={<NotFoundPage />} />
            </Routes>
          </ToastProvider>
        </AuthProvider>
      </BrandContext.Provider>
    </ThemeProvider>
  );
}
